import { type RefObject, useEffect, useRef } from "react";

export type SwipeDirection = "up" | "down" | "left" | "right";

export interface Gestures {
	/** A flick, once it has travelled far enough to mean one. */
	onSwipe?: (direction: SwipeDirection) => void;
	/** A touch that landed and left again without going anywhere. */
	onTap?: () => void;
	/**
	 * Dragging sideways, with how many whole steps have been crossed since the
	 * last report — never nought, and negative for leftwards.
	 *
	 * A count rather than one call per step, because the game it steers can act
	 * on the whole distance at once. Calling per step made a finger that crossed
	 * nine columns into nine separate presses, and a game that applies one press
	 * per simulated step then spends most of a second catching up.
	 *
	 * Set this and sideways flicks stop being swipes: a finger cannot mean both
	 * "three columns that way" and "that way, quickly" on the same axis, and of
	 * the two, dragging is what lets a child place a piece exactly.
	 */
	onDrag?: (steps: number) => void;
	/** How far sideways one drag step is, measured when the finger lands. */
	dragStep?: () => number;
	/** How far a flick has to travel. Below this it is a tap. */
	swipeMin?: number;
	enabled?: boolean;
}

/** Far enough to have meant it, close enough to be a flick and not a journey. */
const DEFAULT_SWIPE_MIN = 28;

/**
 * Touch, for games that are played with a thumb.
 *
 * The arcade games shipped with buttons and a keyboard, which is how they are
 * played on a laptop and not how anybody has played a snake on a phone in
 * fifteen years. A child's first instinct on a full-screen board is to swipe
 * it, and until this hook that did nothing at all.
 *
 * Pointer events rather than touch events, so the same code covers a finger, a
 * stylus and a mouse — and pointer capture, so a swipe that starts on the board
 * and ends past its edge is still a swipe. The element wants `touch-action:
 * none` or the browser will take the gesture for scrolling before it arrives.
 */
export function useGestures(target: RefObject<HTMLElement | null>, gestures: Gestures): void {
	// Held in a ref so a caller that rebuilds its handlers every render — which
	// is every caller, since they close over game state — does not detach and
	// reattach the listeners between one frame and the next.
	const latest = useRef(gestures);
	latest.current = gestures;

	useEffect(() => {
		const element = target.current;
		if (!element || gestures.enabled === false) return;

		let pointer: number | null = null;
		let startX = 0;
		let startY = 0;
		// How far the finger has gone sideways since the last step was reported,
		// so a slow drag across the board still moves a piece a column at a time.
		let dragged = 0;
		let step = 0;
		let swiped = false;
		// Whether the finger has already said something by dragging. A drag that
		// wanders out and comes back finishes where it started, and without this
		// that counts as a tap — so placing a block would sometimes turn it.
		let stepped = false;

		const onDown = (event: PointerEvent) => {
			pointer = event.pointerId;
			startX = event.clientX;
			startY = event.clientY;
			dragged = 0;
			swiped = false;
			stepped = false;
			step = latest.current.dragStep?.() ?? 0;
			// Not in jsdom, and not required anywhere: without it a swipe that
			// leaves the element simply ends early.
			element.setPointerCapture?.(event.pointerId);
		};

		const onMove = (event: PointerEvent) => {
			if (pointer !== event.pointerId) return;
			const { onSwipe, onDrag } = latest.current;
			const min = latest.current.swipeMin ?? DEFAULT_SWIPE_MIN;

			const dx = event.clientX - startX;
			const dy = event.clientY - startY;

			if (onDrag && step > 0) {
				// Sideways is a drag. Report whole steps and keep the remainder, so
				// the piece tracks the finger rather than lurching.
				const steps = Math.trunc((dx - dragged) / step);
				if (steps !== 0) {
					onDrag(steps);
					stepped = true;
					dragged += steps * step;
				}
			}

			if (swiped) return;

			const sideways = Math.abs(dx) > Math.abs(dy);
			// A sideways flick is not a swipe when sideways already means dragging.
			if (sideways && onDrag) return;
			if (Math.max(Math.abs(dx), Math.abs(dy)) < min) return;

			swiped = true;
			if (sideways) onSwipe?.(dx > 0 ? "right" : "left");
			else onSwipe?.(dy > 0 ? "down" : "up");
		};

		const onUp = (event: PointerEvent) => {
			if (pointer !== event.pointerId) return;
			pointer = null;
			element.releasePointerCapture?.(event.pointerId);

			/*
			 * A touch that went nowhere and said nothing on the way is a tap,
			 * however long it rested there.
			 *
			 * There was a time limit here as well, on the reasoning that a finger
			 * left on the glass is not a tap. It went because it could not be
			 * tested — the only clock available is the event's own timestamp, and
			 * jsdom reports nought for every event — and a rule that cannot be
			 * exercised is a rule nobody will notice breaking. What it was really
			 * guarding against is covered better by `stepped`.
			 */
			if (swiped || stepped) return;
			const travelled = Math.hypot(event.clientX - startX, event.clientY - startY);
			if (travelled < (latest.current.swipeMin ?? DEFAULT_SWIPE_MIN)) latest.current.onTap?.();
		};

		const onCancel = () => {
			pointer = null;
		};

		element.addEventListener("pointerdown", onDown);
		element.addEventListener("pointermove", onMove);
		element.addEventListener("pointerup", onUp);
		element.addEventListener("pointercancel", onCancel);
		return () => {
			element.removeEventListener("pointerdown", onDown);
			element.removeEventListener("pointermove", onMove);
			element.removeEventListener("pointerup", onUp);
			element.removeEventListener("pointercancel", onCancel);
		};
	}, [target, gestures.enabled]);
}
